import { API_BASE_PATH, ApiCookies, ApiHeaders, type PageMeta } from '@fas/types';
import { useAuthStore } from './auth-store';
import { readyDeviceId } from './device';

export interface ValidationIssue {
  path: string;
  message: string;
}

/** Error thrown for every failed API call; `code` follows the API error catalog (§72). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get issues(): ValidationIssue[] {
    const issues = this.details?.issues;
    return Array.isArray(issues) ? (issues as ValidationIssue[]) : [];
  }
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export function buildUrl(path: string, query?: QueryParams): string {
  const url = `${API_BASE_PATH}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

/** `unreachable`: no answer from the server (offline, or the server is down) — the session may still be valid. */
export type RefreshOutcome = 'ok' | 'rejected' | 'unreachable';

let refreshing: Promise<RefreshOutcome> | null = null;

/**
 * Exchanges the HttpOnly refresh cookie for a new access token. Concurrent callers share
 * one in-flight request so a burst of 401s triggers a single rotation.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  refreshing ??= (async (): Promise<RefreshOutcome> => {
    const csrf = readCookie(ApiCookies.CSRF_TOKEN);
    if (!csrf) return 'rejected';
    try {
      const res = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { [ApiHeaders.CSRF_TOKEN]: csrf, [ApiHeaders.DEVICE_ID]: await readyDeviceId() },
      });
      // A gateway error means the API could not be reached, not that the session ended.
      if (res.status === 502 || res.status === 503 || res.status === 504) return 'unreachable';
      if (!res.ok) return 'rejected';
      const body = (await res.json()) as { data: { accessToken: string } };
      useAuthStore.getState().setAccessToken(body.data.accessToken);
      return 'ok';
    } catch {
      return 'unreachable';
    } finally {
      setTimeout(() => {
        refreshing = null;
      }, 0);
    }
  })();
  return refreshing;
}

export async function refreshAccessToken(): Promise<boolean> {
  return (await refreshSession()) === 'ok';
}

interface RequestOptions {
  body?: unknown;
  query?: QueryParams;
  signal?: AbortSignal;
  /** Attach the bearer token and attempt a silent refresh on expiry (default true). */
  auth?: boolean;
  retry?: boolean;
  headers?: Record<string, string>;
}

const SESSION_OVER = new Set(['SESSION_REVOKED', 'ACCOUNT_INACTIVE', 'TOKEN_INVALID']);

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

export async function apiRequest<T, M = Record<string, unknown>>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: M }> {
  const { body, query, signal, auth = true, retry = true } = options;
  const token = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = { Accept: 'application/json', [ApiHeaders.DEVICE_ID]: await readyDeviceId(), ...options.headers };
  // Multipart bodies get their boundary header from the browser.
  const multipart = isFormData(body);
  if (body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
  if (auth && token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : multipart ? body : JSON.stringify(body),
      credentials: 'include',
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // §63: never pretend an action succeeded when the server could not be reached.
    throw new ApiError(0, 'NETWORK_ERROR', 'Unable to reach the server. Check your connection and try again.');
  }

  const payload = (await res.json().catch(() => null)) as
    | { success: true; data: T; meta: M }
    | { success: false; error: { code: string; message: string; details?: Record<string, unknown> } }
    | null;

  if (res.status === 401 && auth) {
    const code = payload && !payload.success ? payload.error.code : '';
    if (retry && (code === 'TOKEN_EXPIRED' || code === 'UNAUTHENTICATED')) {
      if (await refreshAccessToken()) return apiRequest<T, M>(method, path, { ...options, retry: false });
      useAuthStore.getState().clear();
    } else if (SESSION_OVER.has(code)) {
      useAuthStore.getState().clear();
    }
  }

  if (!res.ok || !payload || payload.success === false) {
    const error = payload && payload.success === false ? payload.error : null;
    throw new ApiError(res.status, error?.code ?? `HTTP_${res.status}`, error?.message ?? 'The request failed', error?.details);
  }
  return { data: payload.data, meta: payload.meta };
}

export const api = {
  get: <T, M = Record<string, unknown>>(path: string, query?: QueryParams, signal?: AbortSignal) =>
    apiRequest<T, M>('GET', path, { query, signal }).then((r) => r.data),
  getPage: <T>(path: string, query?: QueryParams, signal?: AbortSignal) =>
    apiRequest<T[], PageMeta>('GET', path, { query, signal }),
  post: <T>(path: string, body: unknown = {}) => apiRequest<T>('POST', path, { body }).then((r) => r.data),
  patch: <T>(path: string, body: unknown) => apiRequest<T>('PATCH', path, { body }).then((r) => r.data),
  put: <T>(path: string, body: unknown) => apiRequest<T>('PUT', path, { body }).then((r) => r.data),
  delete: <T>(path: string) => apiRequest<T>('DELETE', path).then((r) => r.data),
  /** POST multipart/form-data (file uploads); returns data and meta. */
  upload: <T, M = Record<string, unknown>>(path: string, form: FormData) => apiRequest<T, M>('POST', path, { body: form }),
};

function fileNameFrom(disposition: string | null, fallback: string): string {
  const match = disposition ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition) : null;
  return match ? decodeURIComponent(match[1]) : fallback;
}

/**
 * Downloads a file from an authenticated endpoint (the bearer token cannot ride on a
 * plain link) and hands it to the browser's save dialog.
 */
export async function downloadFile(path: string, query?: QueryParams, fallbackName = 'download', retry = true): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      headers: { [ApiHeaders.DEVICE_ID]: await readyDeviceId(), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      credentials: 'include',
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Unable to reach the server. Check your connection and try again.');
  }
  if (res.status === 401 && retry && (await refreshAccessToken())) return downloadFile(path, query, fallbackName, false);
  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as { error?: { code: string; message: string } } | null;
    throw new ApiError(res.status, payload?.error?.code ?? `HTTP_${res.status}`, payload?.error?.message ?? 'The download failed');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileNameFrom(res.headers.get('Content-Disposition'), fallbackName);
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** Human-readable message for any thrown value. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}
